import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../apiHelpers';
import Panel from '../../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import FadeIn from '../../components/ui/FadeIn';
import { CATEGORY } from '../../components/ui/categoryColors';
import {
  AirspaceStatus,
  AIRSPACE_STATUS_LABELS,
  AIRSPACE_STATUS_PILL_CLASSES,
} from '../../utils/airspace-helpers';

interface QueueItem {
  id: string;
  airspace_status: AirspaceStatus;
}

const STATUSES: AirspaceStatus[] = ['needs_action', 'needs_review', 'needs_authorization', 'pending', 'complete'];

/**
 * Dashboard module: aggregate airspace status counts across upcoming events
 * (within ~60 days). Click a count to open the /pilots/airspace queue
 * filtered to that status. Visible only to pilots, ops, and admins.
 */
const AirspaceAuthModule: React.FC = () => {
  const api = useApi();
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/airspace/queue?within_days=60', { silent: true })
      .then((d: QueueItem[]) => { setQueue(d); setLoading(false); })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts: Record<AirspaceStatus, number> = {
    needs_action: 0,
    needs_review: 0,
    needs_authorization: 0,
    pending: 0,
    complete: 0,
  };
  for (const q of queue) {
    if (q.airspace_status in counts) counts[q.airspace_status]++;
  }
  const totalActive = STATUSES.filter(s => s !== 'complete').reduce((a, s) => a + counts[s], 0);

  return (
    <Panel
      title="Airspace Authorizations"
      titleStyle="section"
      accent="sky"
      lit
      level={3}
      actions={<Link to="/pilots/airspace" className="text-xs hover:brightness-125" style={{ color: CATEGORY.sky.ink.oklch }}>open queue →</Link>}
    >
      <FadeIn key={loading ? 'loading' : 'loaded'}>
      {loading ? (
        <SkeletonGroup label="Loading airspace authorizations" className="space-y-3">
          <Skeleton className="h-4 w-2/3" />
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {STATUSES.map(s => <Skeleton key={s} className="h-16 rounded-panel" />)}
          </div>
        </SkeletonGroup>
      ) : (
        <>
          <div className="text-xs text-on-surface-variant mb-3">
            {totalActive} event{totalActive === 1 ? '' : 's'} need attention in the next 60 days
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {STATUSES.map(s => (
              <Link
                key={s}
                to={`/pilots/airspace?status=${s}`}
                className={`flex flex-col items-center justify-center rounded-md py-3 ring-1 ring-inset ${AIRSPACE_STATUS_PILL_CLASSES[s]} hover:brightness-110 transition`}
                title={AIRSPACE_STATUS_LABELS[s]}
              >
                <span className="text-2xl font-bold tabular-nums">{counts[s]}</span>
                <span className="text-[10px] uppercase tracking-wider mt-1 leading-tight text-center">
                  {AIRSPACE_STATUS_LABELS[s]}
                </span>
              </Link>
            ))}
          </div>
        </>
      )}
      </FadeIn>
    </Panel>
  );
};

export default AirspaceAuthModule;
